import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLinkClickHandler, useSearchParams } from 'react-router'
import { ListFilter } from 'lucide-react'
import { toast } from 'sonner'
import { moderationApi } from '../../services/api'
import { useOrganization } from '../../hooks/useOrganization'
import { PRODUCT_KEYS } from '../../config/studioProducts'
import {
  Alert,
  Button,
  DataView,
  Dialog,
  Field,
  LinkButton,
  PageHeader,
  PageLayout,
  SegmentedControl,
  StatePanel,
  Textarea,
} from '../../components/common'
import { ReviewQueue } from '../../components/moderation/ReviewQueue'
import { ReviewDecisionPanel } from '../../components/moderation/ReviewDecisionPanel'
import ModerationStateBanner from '../../components/moderation/ModerationStateBanner'
import { useModerationStatus } from '../../components/moderation/useModerationStatus'
import {
  REVIEW_TABS,
  classifyDecisionError,
  decisionPayload,
  isOpenReview,
  overturnDescription,
  pendingTabEmpty,
  reviewShortTitle,
  reviewTabItems,
  selfApprovalDescription,
} from '../../components/moderation/moderationReviewModel'
import styles from '../../components/CommandCenterApprovals.module.css'

const EMPTY = []

const PHRASE_RULES_PATH = '/dashboard/feed/moderation/rules'

// How often an open queue and an open review refresh while the tab is
// visible, so a decision made elsewhere shows without a reload.
const QUEUE_REFRESH_MS = 30 * 1000

// The "Needs review" empty state while moderation does not enforce for the
// brand: nothing comes here, which is not the same as all decided.
const NOT_ENFORCING_EMPTY = 'Nothing is held while moderation is not on for this brand.'

// The review the queue moves to once a decision takes id out of it: the one
// that takes its place, else the new last one.
function reviewAfterDecision(reviews, id) {
  const index = reviews.findIndex((review) => review.id === id)
  const remaining = reviews.filter((review) => review.id !== id)
  return remaining[Math.min(Math.max(index, 0), remaining.length - 1)] || null
}

// ModerationReviewsPage is the organization's moderation queue. Brand admins
// (external_org_admin) and above decide held posts against the immutable
// snapshot each review was opened for.
export default function ModerationReviewsPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const orgSlug = currentOrg?.slug
  const queryClient = useQueryClient()
  const openPhraseRules = useLinkClickHandler(PHRASE_RULES_PATH)
  const [searchParams, setSearchParams] = useSearchParams()
  // A notification links straight to one review.
  const linkedReviewId = searchParams.get('review')
  // Phrase rules are Feed configuration; the queue only needs publishing.
  const canEditRules = Boolean(hasProductAccess?.(PRODUCT_KEYS.FEED, 'configure'))
  const [tab, setTab] = useState(REVIEW_TABS[0].value)
  const [selectedId, setSelectedId] = useState(linkedReviewId)
  // A linked review opens straight away, at phone width too.
  const [mobileReviewOpen, setMobileReviewOpen] = useState(Boolean(linkedReviewId))
  // The open decision dialog, bound to the review it was opened on:
  // { kind: 'reject' | 'approve-self' | 'overturn', target } or null.
  // Confirming always decides target, whatever the page shows behind it.
  const [dialog, setDialog] = useState(null)
  const [reason, setReason] = useState('')
  const [dialogError, setDialogError] = useState(null)
  // Why the last decision on one review was refused: { reviewId, tone,
  // message, reason } or null. It is only ever shown on that review.
  const [notice, setNotice] = useState(null)
  // A brand switch (switchOrganization keeps this page) starts over on the
  // new brand's queue: the pinned review, the decision dialog with its reason
  // and error, and the notice all belong to the old brand, which the new
  // brand answers 404 for. The pin holds only within one brand. A
  // notification link that switched the brand is applied after this, so it
  // still opens its review.
  const [orgSeen, setOrgSeen] = useState(orgSlug)
  if (orgSlug !== orgSeen) {
    setOrgSeen(orgSlug)
    setSelectedId(null)
    setMobileReviewOpen(false)
    setNotice(null)
    setDialog(null)
    setReason('')
    setDialogError(null)
  }
  // A notification link can arrive while the page is open: it selects its
  // review and opens it, as on first load. Dropping the link (null) changes
  // nothing.
  const [linkSeen, setLinkSeen] = useState(linkedReviewId)
  if (linkedReviewId !== linkSeen) {
    setLinkSeen(linkedReviewId)
    if (linkedReviewId) {
      setSelectedId(linkedReviewId)
      setMobileReviewOpen(true)
      setNotice(null)
      setDialog(null)
      setReason('')
      setDialogError(null)
    }
  }
  const decisionStartRef = useRef(null)
  const selectedQueueButtonRef = useRef(null)
  const pendingFocusRef = useRef(null)

  const activeTab = REVIEW_TABS.find((entry) => entry.value === tab) || REVIEW_TABS[0]
  const listKey = ['moderation-reviews', orgSlug, activeTab.status]
  const reviewsQuery = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) => moderationApi.listReviews(orgSlug, {
      status: activeTab.status,
      limit: 25,
      ...(pageParam ? { cursor: pageParam } : {}),
    }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage?.next_cursor || undefined,
    enabled: Boolean(orgSlug),
    refetchInterval: QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
  })
  const reviews = useMemo(
    () => (reviewsQuery.data?.pages || []).flatMap((page) => page?.reviews || EMPTY),
    [reviewsQuery.data],
  )
  // Every page carries the same open-tab counts; the first is enough.
  const counts = reviewsQuery.data?.pages?.[0]?.counts
  const pendingEmpty = pendingTabEmpty(counts)
  const statusQuery = useModerationStatus(orgSlug)
  const notEnforcing = Boolean(statusQuery.data) && !statusQuery.data.enforcing
  // Pin the first review once the list has loaded, so a refreshed list can
  // never swap the review being read for another one.
  if (selectedId === null && reviews.length > 0) setSelectedId(reviews[0].id)
  const listed = reviews.some((entry) => entry.id === selectedId)
  // The selected review may have left the list (decided elsewhere, deleted,
  // expired, or reached through a link): it stays selected and the panel
  // shows its current state.
  const selectedOutsideList = Boolean(selectedId) && !listed
  // With no row to select (a linked review the list no longer carries), the
  // workspace still renders so the panel can say why it cannot be decided.
  const selectedOnly = reviews.length === 0 && Boolean(selectedId)
  // At phone width the queue and the detail take turns. With no row to
  // select, the page opens on the detail.
  const detailOpen = mobileReviewOpen || selectedOnly
  const nextReview = reviews.find((entry) => entry.id !== selectedId) || null

  const reviewQuery = (id) => ({
    queryKey: ['moderation-review', orgSlug, id],
    queryFn: () => moderationApi.getReview(orgSlug, id),
  })
  const detailQuery = useQuery({
    ...reviewQuery(selectedId),
    enabled: Boolean(orgSlug && selectedId),
    refetchOnWindowFocus: true,
    // A decided review changes no more; only an open one is polled.
    refetchInterval: (query) => (isOpenReview(query.state.data) ? QUEUE_REFRESH_MS : false),
  })

  // Once the operator moves off the linked review, the link no longer
  // describes the page: drop it so a reload or a shared URL does not reopen it.
  const dropLinkedReview = () => {
    if (!searchParams.has('review')) return
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('review')
      return next
    }, { replace: true })
  }

  useLayoutEffect(() => {
    if (!pendingFocusRef.current) return
    const target = pendingFocusRef.current === 'queue' ? selectedQueueButtonRef : decisionStartRef
    target.current?.focus()
    pendingFocusRef.current = null
  })

  // captureTarget freezes what a decision is about when its control is
  // used: the review id, its title for the dialog, whether the viewer wrote
  // it, whether it overturns an automatic rejection, and the versions shown.
  const captureTarget = (shown) => ({
    id: shown.id,
    title: reviewShortTitle(shown),
    author: shown.viewer_is_author === true,
    autoRejected: shown.status === 'auto_rejected',
    review: shown,
  })

  const openDialog = (kind, shown) => {
    setReason('')
    setDialogError(null)
    setNotice((current) => (current?.reviewId === shown.id ? null : current))
    setDialog({ kind, target: captureTarget(shown) })
  }

  const closeDialog = () => {
    setDialog(null)
    setReason('')
    setDialogError(null)
  }

  const selectReview = (id) => {
    if (window.innerWidth <= 768) pendingFocusRef.current = 'decision'
    if (id !== linkedReviewId) dropLinkedReview()
    setSelectedId(id)
    setNotice(null)
    setMobileReviewOpen(true)
  }

  // invalidateDecisionViews refreshes every view of a post's moderation state
  // after a decision: the queue, the reviews, Manage posts and the composer.
  // Every review, not only the decided one: a decision reaches the other
  // posts of its regulation-variant family (an approval releases their
  // reviews of the same content), which the queue does not identify.
  const invalidateDecisionViews = () => {
    queryClient.invalidateQueries({ queryKey: ['moderation-reviews', orgSlug] })
    queryClient.invalidateQueries({ queryKey: ['moderation-review', orgSlug] })
    queryClient.invalidateQueries({ queryKey: ['posts', orgSlug] })
    queryClient.invalidateQueries({ queryKey: ['feed-ops'] })
    queryClient.invalidateQueries({ queryKey: ['post-moderation', orgSlug] })
  }

  const selectTab = (value) => {
    setTab(value)
    dropLinkedReview()
    setSelectedId(null)
    setNotice(null)
    closeDialog()
  }

  // A decision's next review is read while the decision is sent, so it opens
  // from cache when the decision lands instead of loading after it.
  const prefetchReviewAfter = (id) => {
    const next = reviewAfterDecision(reviews, id)
    if (next) queryClient.prefetchQuery(reviewQuery(next.id))
  }

  const completeDecision = (id, message) => {
    const next = reviewAfterDecision(reviews, id)
    queryClient.setQueryData(listKey, (current) => current && {
      ...current,
      pages: current.pages.map((page) => ({ ...page, reviews: (page.reviews || []).filter((review) => review.id !== id) })),
    })
    queryClient.removeQueries({ queryKey: ['moderation-review', orgSlug, id], exact: true })
    pendingFocusRef.current = 'decision'
    dropLinkedReview()
    setSelectedId(next?.id || null)
    setMobileReviewOpen(Boolean(next))
    closeDialog()
    setNotice(null)
    toast.success(message)
    // The next review opens from the read taken while the decision was sent,
    // and is read again with the rest.
    invalidateDecisionViews()
  }

  // handleDecisionError keeps a refusal on the review it was about. A stale
  // refusal closes the dialog and refreshes that review, which stays in
  // front with why nothing was recorded; nothing moves to another post.
  // Any other failure stays in the dialog it came from, reason kept.
  const handleDecisionError = (error, { target, fromDialog }, verb) => {
    const refusal = classifyDecisionError(error)
    if (refusal.kind === 'stale') {
      closeDialog()
      setSelectedId(target.id)
      setNotice({ reviewId: target.id, tone: 'warning', message: refusal.message, reason: refusal.reason })
      queryClient.invalidateQueries({ queryKey: ['moderation-review', orgSlug, target.id] })
      queryClient.invalidateQueries({ queryKey: ['moderation-reviews', orgSlug] })
      pendingFocusRef.current = 'decision'
      toast.error(refusal.message)
      return
    }
    if (fromDialog) setDialogError(refusal.message)
    else setNotice({ reviewId: target.id, tone: 'error', message: refusal.message, reason: '' })
    toast.error(`Failed to ${verb}: ${refusal.message}`)
  }

  const approveMutation = useMutation({
    mutationFn: ({ target }) => moderationApi.approveReview(orgSlug, target.id, decisionPayload(target.review)),
    onMutate: ({ target }) => {
      setDialogError(null)
      prefetchReviewAfter(target.id)
    },
    onSuccess: (_data, { target }) => completeDecision(target.id, 'Post approved'),
    onError: (error, variables) => handleDecisionError(error, variables, 'approve'),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ target, reason: text }) => moderationApi.rejectReview(orgSlug, target.id, decisionPayload(target.review, text)),
    onMutate: ({ target }) => {
      setDialogError(null)
      prefetchReviewAfter(target.id)
    },
    onSuccess: (_data, { target }) => completeDecision(target.id, 'Post rejected'),
    onError: (error, variables) => handleDecisionError(error, variables, 'reject'),
  })

  // An overturn approves a rejection from History. The review stays
  // selected: it is still in History, now approved. The current rules may
  // still hold the post: new findings open another review (new_review_id),
  // or reject it again automatically (rejected_again, new_review_id is
  // that auto-rejected review), and then it stays hidden.
  const overturnMutation = useMutation({
    mutationFn: ({ target, reason: text }) => moderationApi.overturnReview(orgSlug, target.id, decisionPayload(target.review, text)),
    onMutate: () => setDialogError(null),
    onSuccess: (data) => {
      closeDialog()
      setNotice(null)
      if (data?.rejected_again) {
        toast.warning('The rejection was overturned, but the current rules rejected the post again. It stays hidden; the new rejection is on the Auto-rejected tab.')
      } else {
        toast.success(data?.new_review_id
          ? 'Rejection overturned. Moderation found something new since, so the post needs one more review.'
          : data?.checking
            ? 'Rejection overturned. The moderation rules changed since, so the post is checked again before it goes live.'
            : 'Rejection overturned')
      }
      invalidateDecisionViews()
    },
    onError: (error, variables) => handleDecisionError(error, variables, 'overturn'),
  })

  const deciding = approveMutation.isPending || rejectMutation.isPending || overturnMutation.isPending
  const review = detailQuery.data

  if (!orgSlug) {
    return (
      <PageLayout size="full">
        <StatePanel state="restricted" title="Select an organization" description="Choose an organization before opening its moderation queue." />
      </PageLayout>
    )
  }

  return (
    <PageLayout size="full">
      <PageHeader
        title="Moderation"
        context="Feed / Moderation"
        subtitle="Posts held for your review."
        actions={canEditRules ? (
          <LinkButton href={PHRASE_RULES_PATH} variant="secondary" onClick={openPhraseRules}>
            <ListFilter size={17} aria-hidden="true" />
            Phrase rules
          </LinkButton>
        ) : null}
      />

      <ModerationStateBanner orgSlug={orgSlug} context="queue" />

      <SegmentedControl
        label="Review status"
        value={tab}
        onValueChange={selectTab}
        items={reviewTabItems(counts)}
      />

      <DataView
        items={reviews.length > 0 || !selectedId ? reviews : [selectedId]}
        loading={reviewsQuery.isLoading}
        error={reviewsQuery.error}
        emptyTitle={tab === 'decided' ? 'No decisions yet' : tab === 'pending' ? pendingEmpty.title : 'Nothing to review'}
        emptyDescription={tab === 'auto_rejected'
          ? 'No post was rejected automatically.'
          : tab === 'decided' ? 'Approved, rejected and superseded reviews appear here.'
            : notEnforcing && !pendingEmpty.showAutoRejected ? NOT_ENFORCING_EMPTY : pendingEmpty.description}
        primaryAction={tab === 'pending' && pendingEmpty.showAutoRejected
          ? <Button variant="secondary" onClick={() => selectTab('auto_rejected')}>Show auto-rejected</Button>
          : undefined}
        onRetry={() => reviewsQuery.refetch()}
      >
        <div className={`${styles.workspace} ${detailOpen ? styles.mobileReviewOpen : ''}`.trim()}>
          <div className={styles.queuePane}>
            <ReviewQueue
              reviews={reviews}
              title={activeTab.label}
              selectedId={selectedId}
              selectedButtonRef={selectedQueueButtonRef}
              disabled={deciding}
              hasMore={Boolean(reviewsQuery.hasNextPage)}
              onLoadMore={() => reviewsQuery.fetchNextPage()}
              onSelect={selectReview}
            />
          </div>
          <div className={styles.decisionPane}>
            <ReviewDecisionPanel
              key={selectedId || 'none'}
              review={review}
              loading={detailQuery.isLoading}
              error={detailQuery.error}
              notice={notice && notice.reviewId === selectedId ? notice : null}
              deciding={deciding}
              onRetry={() => detailQuery.refetch()}
              onApprove={() => {
                if (!review) return
                // The author confirms first; the decision is still allowed
                // and recorded as a self-review.
                if (review.viewer_is_author === true) openDialog('approve-self', review)
                else approveMutation.mutate({ target: captureTarget(review), fromDialog: false })
              }}
              onReject={() => review && openDialog('reject', review)}
              onOverturn={() => review && openDialog('overturn', review)}
              onNext={nextReview && (selectedOutsideList || notice?.reviewId === selectedId) ? () => selectReview(nextReview.id) : undefined}
              onOpenReview={selectReview}
              onBack={() => {
                if (selectedOnly) {
                  // The queue behind a review the list does not carry is
                  // empty: going back drops the link and shows the empty
                  // queue.
                  dropLinkedReview()
                  setSelectedId(null)
                  setMobileReviewOpen(false)
                  return
                }
                pendingFocusRef.current = 'queue'
                setMobileReviewOpen(false)
              }}
              startFocusRef={decisionStartRef}
            />
          </div>
        </div>
      </DataView>

      <Dialog
        open={dialog?.kind === 'reject'}
        title="Reject post"
        description={dialog?.kind === 'reject'
          ? `“${dialog.target.title}” stays hidden and this revision is never published. The author is notified with your reason.`
          : ''}
        dismissable={!rejectMutation.isPending}
        onClose={() => { if (!rejectMutation.isPending) closeDialog() }}
        footer={(
          <>
            <Button variant="secondary" disabled={rejectMutation.isPending} onClick={closeDialog}>Cancel</Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              disabled={!reason.trim()}
              onClick={() => rejectMutation.mutate({ target: dialog.target, reason, fromDialog: true })}
            >
              Reject post
            </Button>
          </>
        )}
      >
        {dialogError ? <Alert variant="error">{dialogError}</Alert> : null}
        <Field label="Reason" help="Required. Recorded with the decision and shown to the author.">
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
        </Field>
      </Dialog>

      <Dialog
        open={dialog?.kind === 'approve-self'}
        title="You wrote this post"
        description={dialog?.kind === 'approve-self' ? selfApprovalDescription(dialog.target) : ''}
        dismissable={!approveMutation.isPending}
        onClose={() => { if (!approveMutation.isPending) closeDialog() }}
        footer={(
          <>
            <Button variant="secondary" disabled={approveMutation.isPending} onClick={closeDialog}>Cancel</Button>
            <Button loading={approveMutation.isPending} onClick={() => approveMutation.mutate({ target: dialog.target, fromDialog: true })}>
              {dialog?.target.autoRejected ? 'Overturn and approve my post' : 'Approve my post'}
            </Button>
          </>
        )}
      >
        {dialogError ? <Alert variant="error">{dialogError}</Alert> : null}
      </Dialog>

      <Dialog
        open={dialog?.kind === 'overturn'}
        title="Overturn the rejection"
        description={dialog?.kind === 'overturn' ? overturnDescription(dialog.target) : ''}
        dismissable={!overturnMutation.isPending}
        onClose={() => { if (!overturnMutation.isPending) closeDialog() }}
        footer={(
          <>
            <Button variant="secondary" disabled={overturnMutation.isPending} onClick={closeDialog}>Cancel</Button>
            <Button
              loading={overturnMutation.isPending}
              disabled={!reason.trim()}
              onClick={() => overturnMutation.mutate({ target: dialog.target, reason, fromDialog: true })}
            >
              Overturn and approve
            </Button>
          </>
        )}
      >
        {dialog?.target.author ? <Alert variant="info">You wrote this post. History records this as a self-review.</Alert> : null}
        {dialogError ? <Alert variant="error">{dialogError}</Alert> : null}
        <Field label="Reason" help="Required. Recorded with the decision and shown to the author.">
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
        </Field>
      </Dialog>
    </PageLayout>
  )
}
